import React from 'react'
import { connect } from 'react-redux'
import { actionCreators } from '@/store/CodeLists'
import Icon from '@/components/Icon'
import Resx from '@/components/Resx'
import { Main, MainTop, MainTopTitle, MainTopLeft, MainBody } from '@/components/Layout'
import { actionCreators as dialogActions } from '@/store/Dialogs'
import {
  Button
} from 'reactstrap'
import { dialogTypes } from '@/dialogs'
import LoadingIndicator from '@/components/LoadingIndicator'
import { format } from '@/utils/StringUtilities'
import { localized } from '@/decorators/localized'
import { formatDate } from '@/utils/dateUtilities'
import { withPermissions } from '@/decorators/withPermissions'

function mapStateToProps (state) {
  return {
    codeListValidInvalid: state.codeLists.codeListValidInvalid,
    codeListDetail: state.codeLists.codeListDetail,
    processing: state.codeLists.processing,
    navigationEntities: state.codeLists.navigationEntities
  }
}

const mapDispatchToProps = {
  createItem: actionCreators.createItem,
  updateItem: actionCreators.updateItem,
  deleteItem: actionCreators.deleteItem,
  restoreItem: actionCreators.restoreItem,
  getNavigationEntities: actionCreators.getNavigationEntities,
  openDialog: dialogActions.openDialog,
  getCodeListDetail: actionCreators.getCodeListDetail
}

@localized()
@withPermissions()
@connect(mapStateToProps, mapDispatchToProps)
class CodeListEditor extends React.Component {
  constructor (props, context) {
    super(props, context)

    this.state = {
      codeListDetail: props.codeListDetail,
      currentCodeListType: props.match.params.type,
      processing: props.processing,
      navigationEntities: props.navigationEntities,
      editForm: null
    }
  }

  componentDidMount () {
    this.props.getCodeListDetail(this.state.currentCodeListType)
    this.props.getNavigationEntities(this.state.currentCodeListType)
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    return {
      codeListDetail: nextProps.codeListDetail,
      processing: nextProps.processing,
      navigationEntities: nextProps.navigationEntities
    }
  }

  deleteItem (id) {
    this.props.openDialog({
      dialogType: dialogTypes.confirm,
      title: 'Common.DeleteDialogTitle',
      content: 'Common.DeleteDialogText',
      primaryButtonText: 'Common.Confirm',
      onAction: () => {
        this.props.deleteItem(id)
      }
    })
  }

  restoreItem (id) {
    this.props.openDialog({
      dialogType: dialogTypes.confirm,
      title: 'CodeLists.RestoreDialogTitle',
      content: 'CodeLists.RestoreDialogText',
      primaryButtonText: 'Common.Confirm',
      onAction: () => {
        this.props.restoreItem(id)
      }
    })
  }

  addItemForm () {
    const { localize } = this.props

    const content = {
      itemType: this.state.currentCodeListType,
      navigationEntities: this.state.navigationEntities
    }

    this.props.openDialog({
      dialogType: dialogTypes.codeListEditor,
      localizedTitle: format(localize('Common.AddCodeListTitle'), localize(`CodeLists.${this.state.currentCodeListType}`)),
      content: content,
      primaryButtonText: 'Common.Confirm',
      onAction: (value) => {
        this.addItem(value)
      }
    })
  }

  addItem (item) {
    this.props.createItem(item)

    this.setState({
      editForm: null
    })
  }

  updateItemForm (item) {
    const { localize } = this.props

    const content = {
      itemType: this.state.currentCodeListType,
      item: item,
      navigationEntities: this.state.navigationEntities
    }

    this.props.openDialog({
      dialogType: dialogTypes.codeListEditor,
      localizedTitle: format(localize('Common.EditCodeListTitle'), localize(`CodeLists.${this.state.currentCodeListType}`)),
      content: content,
      primaryButtonText: 'Common.Confirm',
      onAction: (value) => {
        this.updateItem(value)
      },
      disabled: !this.canManage()
    })
  }

  updateItem (item) {
    this.props.updateItem(item)

    this.setState({
      editForm: null
    })
  }

  getValidItemDiv (codeList, disabled) {
    return <div key={codeList.id} className='line-edit'>
      <div className='line-edit__left'>
        <strong>{codeList.name}</strong>
        <i className='d-block'>
          <small>({codeList.latinName})</small>
        </i>
      </div>
      <div className='btn-actions'>
        {!this.state.processing && <a className='btn-icon' onClick={() => this.updateItemForm(codeList)}><Icon name='pencil' /></a>}
        {!this.state.processing && !codeList.isReadOnly && !disabled && <a className='btn-icon' onClick={() => this.deleteItem(codeList.id)}> <Icon name='x' /> </a>}
      </div>
    </div>
  }

  getInvalidItemDiv (codeList, disabled) {
    const codeIsOutToDate = validTo => {
      const today = Date.now()
      return formatDate(today) >= formatDate(validTo)
    }

    const { localize } = this.props
    return <div key={codeList.id} className={codeList.isDeleted || codeIsOutToDate(codeList.validTo) ? 'line-edit deleted' : 'line-edit'}>
      <div className='line-edit__left'>
        <strong>{codeList.name}</strong>
        <i className='d-block'>
          <small>({codeList.latinName})</small>
        </i>
        <div className='info'>
          {codeList.isDeleted && <span className='deleted'>{localize('Common.Deleted')}</span>}
          {codeList.validTo && <span className={codeIsOutToDate(codeList.validTo) ? 'valid-to red' : 'valid-to'}>{localize('Common.ValidTo')}: {formatDate(codeList.validTo)}</span>}
        </div>
      </div>
      <div className='btn-actions'>
        {!this.state.processing && !codeList.isDeleted && <a className='btn-icon' onClick={() => this.updateItemForm(codeList)}><Icon name='pencil' /></a>}
        {!this.state.processing && !codeList.isDeleted && !disabled && <a className='btn-icon' onClick={() => this.deleteItem(codeList.id)}><Icon name='x' /></a>}
        {!this.state.processing && codeList.isDeleted && !disabled && <a className='btn-icon' onClick={() => this.restoreItem(codeList.id)}><Icon name='undo' /></a>}
      </div>
    </div>
  }
  canManage = () => this.props.isGranted('CanManageCodeLists')
  render () {
    const { processing } = this.props
    const { codeListDetail } = this.state

    const disabled = !this.canManage()
    const codeListsDetails = codeListDetail.filter(x => x.isValid).map((codeList) => this.getValidItemDiv(codeList, disabled))
    const invalidCodeListsDetails = codeListDetail.filter(x => !x.isValid).map((codeList) => this.getInvalidItemDiv(codeList, disabled))
    return (
      <Main>
        <MainTop>
          <MainTopTitle backUrl='/code-lists'><Resx name={`CodeLists.${this.state.currentCodeListType}`} /></MainTopTitle>
          <MainTopLeft>
            {!this.state.processing && <Button disabled={disabled} className='btn-sm' color='primary' onClick={() => this.addItemForm()}><Icon name='plus' />&nbsp;Add</Button>}
          </MainTopLeft>
        </MainTop>

        <MainBody className='code-list'>
          {processing === true
            ? <LoadingIndicator />
            : <React.Fragment>
              <div className='col-3-style'>{codeListsDetails}</div>
              {invalidCodeListsDetails.length > 0
                ? <React.Fragment>
                  <div className='box__title'>
                    <Resx name='CodeLists.InvalidItems' />
                  </div>
                  <div className='col-3-style'>{invalidCodeListsDetails}</div>
                </React.Fragment>
                : <div />
              }
            </React.Fragment>
          }
        </MainBody>
      </Main>
    )
  }
}

export default CodeListEditor



// WEBPACK FOOTER //
// ./src/internal-app/components/codeLists/codeListEditor.js